import {
  FormCheckbox,
  FormLabel,
  FormSectionHeader,
  FormSelect,
  Heading,
  Loader,
  FormInput,
  CustomTableHead,
  CustomTableCell,

} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";

import {
  Button,
  Input,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Label,
  Switch,
} from "@/components/ui";
import { cn, formatAmount } from "@/lib/utils";
import {
  ACCOUNT_NAME,
  CHECK,
  CLEARING_AMOUNT,
  DOC_DATE,
  DOC_NO,
  DOC_TYPE,
  DOCUMENT_TYPE,
  DRCR,
  GL_ACCOUNT,
  LINE_ITEMS,
  OWNERSHIP_ID,
  POSTING_YEAR,
  PROPERTY_ID,
  REMARK,
  SMALL_VARIANCE_GL,
  VARIANCE_AMT,
  DOCUMENT_ID
} from "./consts";

const AddEdit = () => {
  const {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    requiredFields,
    onCancel,
    balanceAmount,
    breadcrumbTitle,
    isDocumentTypeLoading,
    documentTypes,
    keys,
    isLoadingGlMaster,
    isView,
    glData,
    isLoadingProperty,
    propertyList,
    ownerList,
    isLoadingLineItems,
    isAddClearingPending,
    isClearingDoc,
    isEntryView,
    setIsEntryView,
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between sticky top-[10px] z-10 bg-g-10">
        <Heading
          title={breadcrumbTitle}
          breadcrumbData={breadcrumbData}
          showBack
          onBackClick={onBackClick}
        />
        {isLoadingGlMaster ? (
          <></>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 mt-[20px] gap-[10px]">
            <div className="relative">
              <FormLabel>
                {"Posting Year"}
                <span className="text-red-700">{"*"}</span>
              </FormLabel>
              <Input
                name={POSTING_YEAR}
                placeholder="Please enter"
                value={form.watch(POSTING_YEAR)}
                disabled
              />
            </div>
            {isView ? <></> : <div className="py-2 flex items-center justify-end">
              Balance Amount:{"  "}
              <span
                className={cn(
                  balanceAmount == 0 ? "text-green-600" : "text-red-600",
                  "ml-2"
                )}
              >
                {formatAmount(balanceAmount, 2)}
              </span>
            </div>}
          </div>
        )}
      </div>

      {isDocumentTypeLoading ||
        isLoadingGlMaster ||
        isLoadingProperty ||
        isClearingDoc ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[10px] rounded-lg px-[30px] py-[15px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] items-center">
                <FormSectionHeader>Document Header</FormSectionHeader>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-[25px]">
                <FormInput
                  name={DOCUMENT_ID}
                  control={form.control}
                  label="Document Id"
                  placeholder="Please enter"
                  error={errors[DOCUMENT_ID]?.message}
                  isRequired={requiredFields.includes(DOCUMENT_ID)}
                  disabled
                />
                <FormSelect
                  key={keys.documentTypes}
                  name={DOCUMENT_TYPE}
                  control={form.control}
                  label="Document Type"
                  placeholder="Please select"
                  error={errors[DOCUMENT_TYPE]?.message}
                  isRequired={requiredFields.includes(DOCUMENT_TYPE)}
                  options={(documentTypes || [])?.map((data: any) => ({
                    value: data?.documentTypeId?.toString(),
                    label: data?.documentType,
                  }))}
                  disabled
                />
                <FormSelect
                  key={keys.propertyList}
                  name={PROPERTY_ID}
                  control={form.control}
                  label="Property Name"
                  placeholder="Please select"
                  error={errors[PROPERTY_ID]?.message}
                  isRequired={requiredFields.includes(PROPERTY_ID)}
                  options={(propertyList?.data || [])?.map((data: any) => ({
                    value: data?.id?.toString(),
                    label: data?.name,
                  }))}
                  onChange={() => {
                    form.setValue(OWNERSHIP_ID, "");
                    form.setValue(LINE_ITEMS, []);
                  }}
                  disabled={isView}
                />
                <FormSelect
                  key={keys.ownerList}
                  name={OWNERSHIP_ID}
                  control={form.control}
                  label="Business Partner"
                  placeholder="Please select"
                  error={errors[OWNERSHIP_ID]?.message}
                  isRequired={requiredFields.includes(OWNERSHIP_ID)}
                  options={(ownerList?.data || [])?.map((data: any) => ({
                    value: data?.id?.toString(),
                    label: data?.name,
                  }))}
                  disabled={isView || !form.watch(PROPERTY_ID)}
                />
                <FormSelect
                  key={keys.glList}
                  name={SMALL_VARIANCE_GL}
                  control={form.control}
                  label="Small Variance GL"
                  placeholder="Please enter"
                  error={errors[SMALL_VARIANCE_GL]?.message}
                  isRequired={requiredFields.includes(SMALL_VARIANCE_GL)}
                  options={(glData?.data || [])
                    ?.filter((data: any) => data.glAccountNumber == "404")
                    ?.map((data: any) => {
                      return {
                        label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                        value: data?.glMasterId?.toString(),
                      };
                    })}
                  onChange={(data: any) => {
                    form.setValue(
                      ACCOUNT_NAME,
                      glData?.data?.find((val: any) => val?.glMasterId == data)
                        ?.accountShortDesc
                    );
                  }}
                  onClear={() => {
                    form.setValue(SMALL_VARIANCE_GL, null);
                    form.setValue(ACCOUNT_NAME, "");
                  }}
                  disabled={isView}
                />
                <FormInput
                  name={ACCOUNT_NAME}
                  control={form.control}
                  label="Account Name"
                  placeholder="Please enter"
                  error={errors[ACCOUNT_NAME]?.message}
                  isRequired={requiredFields.includes(ACCOUNT_NAME)}
                  disabled
                />
                <FormInput
                  name={VARIANCE_AMT}
                  control={form.control}
                  label="Variance Amount"
                  placeholder="Please enter"
                  error={errors[VARIANCE_AMT]?.message}
                  isRequired={requiredFields.includes(VARIANCE_AMT)}
                  disabled={isView || !form.watch(SMALL_VARIANCE_GL)}
                  startIcon="$"
                  variant="float"
                  maxLength="10"
                />
                <div className="lg:col-span-4">
                  <FormInput
                    name={REMARK}
                    control={form.control}
                    label="Remark"
                    placeholder="Please enter"
                    error={errors[REMARK]?.message}
                    isRequired={requiredFields.includes(REMARK)}
                    disabled={isView}
                  />
                </div>
              </div>
            </div>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[15px]">
              <div className={cn("grid grid-cols-1")}>
                <FormSectionHeader className="">Line Items</FormSectionHeader>
              </div>

              {isView && (
                <div className="w-full flex items-center justify-end gap-2 h-[38px] mb-5">
                  <Label className="text-[10px]">GL View</Label>
                  <Switch checked={isEntryView} onCheckedChange={setIsEntryView} />
                  <Label className="text-[10px]">Entry View</Label>
                </div>
              )}

              <div className="overflow-x-auto w-full">
                <div className="min-w-[2000px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="mb-3 rounded">
                        <CustomTableHead className="w-[30px]"></CustomTableHead>
                        <CustomTableHead className="w-[150px]">
                          {isEntryView ? "Customer Subledger ID" : "GL Account"}
                        </CustomTableHead>
                        <CustomTableHead className="w-[150px]">
                          Doc Date
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Doc No.
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Doc Type
                        </CustomTableHead>
                        {/* <CustomTableHead className="w-[100px]">
                          Due Date
                        </CustomTableHead> */}
                        {/* <CustomTableHead className="w-[100px]">
                          Payment Method
                        </CustomTableHead> */}
                        <CustomTableHead className="w-[100px]">
                          Dr/Cr
                        </CustomTableHead>
                        {/* <CustomTableHead className="w-[100px]">
                          Amount
                        </CustomTableHead> */}
                        <CustomTableHead className="w-[150px]">
                          Clearing Amount
                        </CustomTableHead>
                        {/* <CustomTableHead className="w-[100px]">
                          Pay Block
                        </CustomTableHead> */}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingLineItems ? (
                        <Loader />
                      ) : form.watch(LINE_ITEMS)?.length ? (
                        form.watch(LINE_ITEMS)?.map((_: any, index: number) => {
                          return (
                            <TableRow
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <CustomTableCell className="w-[30px]">
                                <FormCheckbox
                                  name={`${LINE_ITEMS}.${index}.${CHECK}`}
                                  control={form.control}
                                  label=""
                                  placeholder=""
                                  error={
                                    (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                      CHECK
                                    ]?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${CHECK}`
                                  )}
                                  disabled={isView}
                                />
                              </CustomTableCell>
                              <CustomTableCell className="w-[150px]">
                                {isEntryView ? (
                                  <FormInput
                                    name={`${LINE_ITEMS}.${index}.customerSubledgerId`}
                                    control={form.control}
                                    label=""
                                    disabled
                                  />
                                ) : (
                                  <FormSelect
                                  name={`${LINE_ITEMS}.${index}.${GL_ACCOUNT}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[
                                      `${LINE_ITEMS}.${index}.${GL_ACCOUNT}`
                                    ]?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${GL_ACCOUNT}`
                                  )}
                                  options={(glData?.data || [])
                                    ?.sort((a: any, b: any) =>
                                      a.glAccountNumber?.localeCompare(
                                        b.glAccountNumber
                                      )
                                    )
                                    ?.map((data: any) => {
                                      return {
                                        label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                                        value: data?.glMasterId?.toString(),
                                      };
                                    })}
                                  disabled
                                />
                                )}
                              </CustomTableCell>
                              <CustomTableCell className="w-[150px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${DOC_DATE}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${DOC_DATE}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${DOC_DATE}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell>
                              <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${DOC_NO}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${DOC_NO}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${DOC_NO}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell>
                              <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${DOC_TYPE}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${DOC_TYPE}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${DOC_TYPE}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell>
                              {/* <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${DUE_DATE}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${DUE_DATE}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${DUE_DATE}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell> */}
                              {/* <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${PAYMENT_METHOD}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[
                                      `${LINE_ITEMS}.${index}.${PAYMENT_METHOD}`
                                    ]?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${PAYMENT_METHOD}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell> */}
                              <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${DRCR}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${DRCR}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${DRCR}`
                                  )}
                                  disabled
                                />
                              </CustomTableCell>
                              {/* <CustomTableCell className="w-[100px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${AMOUNT}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[`${LINE_ITEMS}.${index}.${AMOUNT}`]
                                      ?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${AMOUNT}`
                                  )}
                                  startIcon="$"
                                  disabled
                                />
                              </CustomTableCell> */}
                              <CustomTableCell className="w-[150px]">
                                <FormInput
                                  name={`${LINE_ITEMS}.${index}.${CLEARING_AMOUNT}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Please enter"
                                  error={
                                    errors[
                                      `${LINE_ITEMS}.${index}.${CLEARING_AMOUNT}`
                                    ]?.message
                                  }
                                  isRequired={requiredFields.includes(
                                    `${LINE_ITEMS}.${index}.${CLEARING_AMOUNT}`
                                  )}
                                  startIcon="$"
                                  disabled
                                />
                              </CustomTableCell>
                              {/* <CustomTableCell className="w-[100px]">
                                <div className="flex items-center justify-center">
                                  <FormCheckbox
                                    name={`${LINE_ITEMS}.${index}.${PAY_BLOCK}`}
                                    control={form.control}
                                    label=""
                                    placeholder=""
                                    error={
                                      errors[
                                        `${LINE_ITEMS}.${index}.${PAY_BLOCK}`
                                      ]?.message
                                    }
                                    isRequired={requiredFields.includes(
                                      `${LINE_ITEMS}.${index}.${PAY_BLOCK}`
                                    )}
                                    disabled
                                  />
                                </div>
                              </CustomTableCell> */}
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow className="col-span-13">
                          <CustomTableCell>No Data Found.</CustomTableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3  py-3">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={isAddClearingPending}
              >
                Cancel
              </Button>
              {!isView ? (
                <Button type="submit" disabled={isAddClearingPending}>
                  {isAddClearingPending ? (
                    <Loader variant="secondary" />
                  ) : (
                    <></>
                  )}{" "}
                  Post
                </Button>
              ) : (
                <></>
              )}
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
};

export default AddEdit;
